import type { ice } from '@halvard/rules';
import { z } from 'zod';
import { ErrorCodeSchema } from './errors.js';
import { GameIdSchema } from './primitives.js';
import { PlayerInfoSchema } from './server.js';
import type { Equals } from './types.js';

/**
 * Messages for live games on the ice, served at the `/ice` WebSocket path next to v0.1's.
 *
 * Online play is a match: two games on the same lake with the sides swapped (or a single game).
 * The server is authoritative. Clients never see a crack seed while its game is running, so
 * they don't replay records: every update carries the new public state, plus the events to
 * animate. A game's full record, seed included, is sent when that game ends.
 */
export const ICE_PROTOCOL_VERSION = 1;

const coordinate = z.number().int().min(0).max(8);
/** Slides may land one square off the lake. */
const offLake = z.number().int().min(-1).max(9);
const hash = z.string().regex(/^[0-9a-f]{8}$/);

export const SquareSchema = z.object({ r: coordinate, c: coordinate }).strict();
const SlideSquareSchema = z.object({ r: offLake, c: offLake }).strict();
export const SideSchema = z.enum(['brand', 'rime']);
export const WarriorTypeSchema = z.enum(['skier', 'spear', 'huscarl']);
export const IceWinReasonSchema = z.enum(['glory', 'held', 'sunk', 'resign']);
export const SeatSchema = z.enum(['host', 'guest']);
const warriorId = z.string().regex(/^[a-z]+-[a-z]+-\d{1,2}$/);

export const IceActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('place'), type: WarriorTypeSchema, at: SquareSchema }).strict(),
  z.object({ kind: z.literal('move'), id: warriorId, to: SquareSchema }).strict(),
  z
    .object({ kind: z.literal('shove'), id: warriorId, from: SquareSchema, target: SquareSchema })
    .strict(),
  z.object({ kind: z.literal('pass') }).strict(),
]);

const WarriorSchema = z
  .object({
    id: warriorId,
    side: SideSchema,
    type: WarriorTypeSchema,
    r: coordinate,
    c: coordinate,
  })
  .strict();
const CrackSchema = z
  .object({ r: coordinate, c: coordinate, thawRound: z.number().int() })
  .strict();
const thickness = z.number().int().min(-1).max(3);
const grid = z.array(z.array(thickness).length(9)).length(9);

/** The whole game state as clients see it. `crackSeed` is 0 until the game is over. */
export const IceStateSchema = z
  .object({
    config: z
      .object({
        roundLimit: z.number().int().min(1).max(60),
        gloryTarget: z.number().int().min(1).max(200),
        crackEvery: z.number().int().min(1).max(60),
        cracksPerGroan: z.number().int().min(0).max(9),
        thawDelay: z.number().int().min(1).max(60),
      })
      .strict(),
    lake: z
      .object({
        seed: z.number().int(),
        heights: grid,
        floes: z.array(
          z
            .object({
              code: z.string().regex(/^F\d\d$/),
              side: z.enum(['A', 'B']),
              rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
            })
            .strict(),
        ),
      })
      .strict(),
    heights: grid,
    crackSeed: z.number().int(),
    phase: z.enum(['placing', 'playing', 'over']),
    turn: SideSchema,
    round: z.number().int().min(1),
    warriors: z.array(WarriorSchema).max(12),
    glory: z.number().int().min(0),
    cracks: z.array(CrackSchema).max(81),
    winner: SideSchema.nullable(),
    winReason: IceWinReasonSchema.nullable(),
    moves: z.array(z.string().max(24)).max(400),
  })
  .strict();

const SlideSchema = z
  .object({ id: warriorId, from: SlideSquareSchema, to: SlideSquareSchema, sunk: z.boolean() })
  .strict();

export const IceEventSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('placed'), warrior: WarriorSchema }).strict(),
  z
    .object({ kind: z.literal('moved'), id: warriorId, path: z.array(SquareSchema).max(8) })
    .strict(),
  z
    .object({
      kind: z.literal('shoved'),
      id: warriorId,
      direction: z.object({ dr: z.number().int(), dc: z.number().int() }).strict(),
      strength: z.number().int(),
      wall: z.number().int(),
      slides: z.array(SlideSchema).max(12),
    })
    .strict(),
  z.object({ kind: z.literal('passed'), side: SideSchema }).strict(),
  z
    .object({ kind: z.literal('glory'), gained: z.number().int(), total: z.number().int() })
    .strict(),
  z
    .object({
      kind: z.literal('thawed'),
      r: coordinate,
      c: coordinate,
      height: thickness,
      sunk: warriorId.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('cracked'),
      r: coordinate,
      c: coordinate,
      thawRound: z.number().int(),
    })
    .strict(),
  z.object({ kind: z.literal('over'), winner: SideSchema, reason: IceWinReasonSchema }).strict(),
]);

export const IceMatchGameSchema = z
  .object({
    attacker: SeatSchema,
    winner: SideSchema,
    reason: IceWinReasonSchema,
    glory: z.number().int(),
    round: z.number().int(),
  })
  .strict();

export const IceMatchOutcomeSchema = z
  .object({
    winner: SeatSchema.nullable(),
    reason: z.enum(['faster', 'glory', 'attack', 'resign', 'level']),
  })
  .strict();

// The schemas must describe exactly the types of the rules package.
export const iceSchemaMatchesRules: [
  Equals<z.infer<typeof IceActionSchema>, ice.IceAction>,
  Equals<z.infer<typeof IceEventSchema>, ice.IceEvent>,
  Equals<z.infer<typeof IceStateSchema>, ice.IceState>,
  Equals<z.infer<typeof IceMatchGameSchema>, ice.IceMatchGame>,
  Equals<z.infer<typeof IceMatchOutcomeSchema>, ice.IceMatchOutcome>,
] = [true, true, true, true, true];

export const IceModeSchema = z.enum(['match', 'single']);
export type IceMode = z.infer<typeof IceModeSchema>;

const matchMessage = <T extends string>(type: T) =>
  z.object({ type: z.literal(type), matchId: GameIdSchema }).strict();

/** Every message a client may send on `/ice`. */
export const IceClientMessageSchema = z.discriminatedUnion('type', [
  /** First message on a connection. */
  z
    .object({
      type: z.literal('hello'),
      protocolVersion: z.number().int(),
      rulesVersion: z.number().int(),
      /** A guest token from an earlier `welcome`, to keep the same identity. */
      token: z.string().min(1).max(4096).optional(),
    })
    .strict(),
  z.object({ type: z.literal('ping'), nonce: z.number().int() }).strict(),
  /** Creates a match; the reply carries the join token for the invite link. */
  z
    .object({
      type: z.literal('match.create'),
      mode: IceModeSchema,
      lake: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('random') }).strict(),
        z
          .object({ kind: z.literal('seed'), seed: z.number().int().min(0).max(0xffffffff) })
          .strict(),
      ]),
      /** The side the creator plays in the first game. */
      side: z.enum(['brand', 'rime', 'random']),
    })
    .strict(),
  /** Takes the open seat with the invite token, returns to one's own seat, or watches. */
  z
    .object({
      type: z.literal('match.join'),
      matchId: GameIdSchema,
      joinToken: z.string().min(1).max(128).optional(),
    })
    .strict(),
  /**
   * Plays an action in game `game`. `seq` is the number of actions already played in that game
   * (its `moves.length`); the server answers `STALE_SEQ` if the game has moved on.
   */
  z
    .object({
      type: z.literal('match.action'),
      matchId: GameIdSchema,
      game: z.number().int().min(0).max(1),
      seq: z.number().int().min(0),
      action: IceActionSchema,
    })
    .strict(),
  /** Gives up: the game is lost and, in a match, the match too. */
  matchMessage('match.resign'),
  matchMessage('match.leave'),
]);
export type IceClientMessage = z.infer<typeof IceClientMessageSchema>;
export type IceClientMessageOf<T extends IceClientMessage['type']> = Extract<
  IceClientMessage,
  { type: T }
>;

export const IceRoleSchema = z.enum(['host', 'guest', 'spectator']);

/** A finished game of the match, with its full record (crack seed included). */
const FinishedGameSchema = z
  .object({ result: IceMatchGameSchema, record: z.string().max(64 * 1024) })
  .strict();

/** Full state of a match, sent on join and on reconnect. */
export const IceSnapshotSchema = z
  .object({
    type: z.literal('match.snapshot'),
    matchId: GameIdSchema,
    role: IceRoleSchema,
    mode: IceModeSchema,
    players: z.object({ host: PlayerInfoSchema, guest: PlayerInfoSchema.nullable() }).strict(),
    connected: z.object({ host: z.boolean(), guest: z.boolean() }).strict(),
    /** The seat that plays Brand in the first game; the sides swap for the second. */
    firstBrand: SeatSchema,
    finished: z.array(FinishedGameSchema).max(2),
    /** The game being played (or the last one, once the match is over). */
    current: z
      .object({ index: z.number().int().min(0).max(1), state: IceStateSchema, hash })
      .strict(),
    outcome: IceMatchOutcomeSchema.nullable(),
  })
  .strict();
export type IceSnapshot = z.infer<typeof IceSnapshotSchema>;

/** One validated action and what it did, broadcast to everyone in the match. */
export const IceUpdateSchema = z
  .object({
    type: z.literal('match.update'),
    matchId: GameIdSchema,
    game: z.number().int().min(0).max(1),
    /** Actions played in the game before this one: the `seq` the action was sent with. */
    seq: z.number().int().min(0),
    action: IceActionSchema,
    /** The seat that played it. */
    by: SeatSchema,
    events: z.array(IceEventSchema).max(64),
    state: IceStateSchema,
    hash,
  })
  .strict();
export type IceUpdate = z.infer<typeof IceUpdateSchema>;

/** Every message the server may send on `/ice`. */
export const IceServerMessageSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('welcome'),
      protocolVersion: z.number().int(),
      rulesVersion: z.number().int(),
      player: PlayerInfoSchema,
      /** The guest token to store and send back in the next `hello`. */
      guestToken: z.string(),
    })
    .strict(),
  z.object({ type: z.literal('pong'), nonce: z.number().int() }).strict(),
  z
    .object({
      type: z.literal('error'),
      code: ErrorCodeSchema,
      message: z.string(),
      matchId: GameIdSchema.optional(),
    })
    .strict(),
  z
    .object({ type: z.literal('match.created'), matchId: GameIdSchema, joinToken: z.string() })
    .strict(),
  IceSnapshotSchema,
  IceUpdateSchema,
  z
    .object({
      type: z.literal('match.resigned'),
      matchId: GameIdSchema,
      game: z.number().int().min(0).max(1),
      by: SeatSchema,
      state: IceStateSchema,
      hash,
    })
    .strict(),
  /** A game ended: its full record, with the crack seed, can now be shown and replayed. */
  z
    .object({
      type: z.literal('match.game-over'),
      matchId: GameIdSchema,
      game: z.number().int().min(0).max(1),
      result: IceMatchGameSchema,
      record: z.string().max(64 * 1024),
    })
    .strict(),
  /** The second game starts on the same lake, sides swapped. */
  z
    .object({
      type: z.literal('match.next'),
      matchId: GameIdSchema,
      game: z.literal(1),
      state: IceStateSchema,
      hash,
    })
    .strict(),
  z
    .object({
      type: z.literal('match.over'),
      matchId: GameIdSchema,
      outcome: IceMatchOutcomeSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('match.joined'),
      matchId: GameIdSchema,
      guest: PlayerInfoSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('player.connection'),
      matchId: GameIdSchema,
      seat: SeatSchema,
      connected: z.boolean(),
    })
    .strict(),
]);
export type IceServerMessage = z.infer<typeof IceServerMessageSchema>;
export type IceServerMessageOf<T extends IceServerMessage['type']> = Extract<
  IceServerMessage,
  { type: T }
>;

/** The state as players may see it: everything but the crack seed, until the game is over. */
export function publicIceState(state: ice.IceState): ice.IceState {
  return state.phase === 'over' ? state : { ...state, crackSeed: 0 };
}
